import React, { useEffect, useState, useCallback } from "react";
import { Trophy, Medal, Gift, Users, CalendarDays } from "lucide-react";
import Navbar from "../components/Navbar";
import PublicNav from "../components/PublicNav";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { logError } from "../lib/logger";
import { CupPodium } from "../components/CupPodium";
import { RulesSheet } from "../components/RulesSheet";

const TELEGRAM = "https://t.me/poolefx";

export const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-").map(Number);
  const s = new Date(y, mo - 1, 1).toLocaleString("it-IT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(2)}%`);
const fmtUsd = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: "always" })} $`);
const tone = (v) => (v == null ? "text-zinc-500" : v >= 0 ? "text-emerald-400" : "text-red-400");
const PODIUM = ["text-amber-400", "text-zinc-300", "text-orange-400"];

const RankBadge = ({ rank }) => (
  <div className={`w-9 h-9 shrink-0 rounded-md flex items-center justify-center font-mono font-bold border ${
    rank <= 3 ? "bg-amber-500/10 border-amber-500/25" : "bg-white/5 border-white/10"} ${PODIUM[rank - 1] || "text-zinc-400"}`}>
    {rank <= 3 ? <Medal size={16} /> : rank}
  </div>
);

const Leaderboard = ({ entries, hideEmpty }) =>
  entries.length === 0 ? (hideEmpty ? null :
    <div data-testid="cup-empty" className="text-sm text-zinc-500 py-10 border border-dashed border-white/10 rounded-xl text-center">
      Classifica non ancora disponibile per questo mese.
    </div>
  ) : (
    <div className="space-y-2" data-testid="cup-leaderboard">
      {entries.map((e) => (
        <div key={e.user_id} data-testid={`cup-row-${e.rank}`}
          className="flex items-center gap-4 rounded-xl border border-white/10 bg-[#141414] px-4 py-3 hover:border-amber-500/30 transition-colors">
          <RankBadge rank={e.rank} />
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold tracking-wide truncate">{e.name}</div>
            <div className="text-[11px] text-zinc-400 font-mono">#{e.rank} in classifica</div>
          </div>
          <div className="text-right">
            <div className={`font-mono text-lg font-bold ${tone(e.return_pct)}`} data-testid={`cup-row-${e.rank}-pct`}>{fmtPct(e.return_pct)}</div>
            <div className={`font-mono text-xs ${tone(e.profit_usd)}`} data-testid={`cup-row-${e.rank}-usd`}>{fmtUsd(e.profit_usd)}</div>
          </div>
        </div>
      ))}
    </div>
  );

const InfoCard = ({ icon: Icon, title, text, testid, empty }) => (
  <div className="rounded-xl border border-white/10 bg-[#141414] p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-3">
      <Icon size={14} className="text-amber-400" /> {title}
    </div>
    <div className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed">{text || <span className="text-zinc-600">{empty}</span>}</div>
  </div>
);

export default function Championship() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback((m) => {
    api.get("/championship", { params: m ? { month: m } : {} })
      .then((r) => setData(r.data)).catch((e) => logError("championship", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {user ? <Navbar /> : <PublicNav />}
      <main className="max-w-4xl mx-auto px-6 py-14 lg:py-20" data-testid="championship-page">
        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-6">
          <Trophy size={13} /> TRADING CUP
        </span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-4">
          Il campionato di trading <span className="text-amber-400">mensile</span>
        </h1>
        <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
          Ogni mese i partecipanti si sfidano a colpi di performance. La classifica è ordinata per
          <span className="text-white font-medium"> rendimento % del mese</span>.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a href={TELEGRAM} target="_blank" rel="noopener noreferrer" data-testid="cup-join-telegram-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-sky-500 text-white font-medium hover:bg-sky-400 transition-colors">
            Partecipa via Telegram
          </a>
          <span className="inline-flex items-center gap-1.5 text-sm text-zinc-400" data-testid="cup-participants">
            <Users size={15} className="text-blue-400" /> {data?.participants_count ?? 0} {data?.participants_count === 1 ? "partecipante" : "partecipanti"}
          </span>
        </div>

        <div className="mt-12 flex flex-wrap items-end justify-between gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <Trophy size={16} className="text-amber-400" /> Classifica · {monthLabel(data?.month)}
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-500">
            <CalendarDays size={14} /> Mese
            <select data-testid="cup-month-select" value={data?.month || ""} onChange={(e) => load(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-[#141414] border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none">
              {(data?.months || []).map((m) => (
                <option key={m} value={m}>{monthLabel(m)}{m === data?.current_month ? " (in corso)" : ""}</option>
              ))}
            </select>
          </label>
        </div>
        {(data?.entries || []).length > 0 && (
          <div className="mb-8 pt-4"><CupPodium entries={data.entries} /></div>
        )}
        <Leaderboard entries={(data?.entries || []).slice(3)} hideEmpty={(data?.entries || []).length > 0} />

        <div className="mt-10">
          <InfoCard icon={Gift} title={`Premi · ${monthLabel(data?.month)}`} text={data?.prizes} testid="cup-prizes" empty="Premi in definizione." />
        </div>
        <div className="mt-12"><RulesSheet text={data?.rules} /></div>

        <p className="mt-10 text-xs text-zinc-400">
          ⓘ Per aderire alla Trading Cup contattaci su Telegram: completate le procedure, lo staff abilita il tuo account.
          Le performance sono verificate e pubblicate dallo staff.
        </p>
      </main>
    </div>
  );
}
